import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, DollarSign, Users, Handshake, BookOpen,
  Newspaper, Image, Award, MessageSquare, Mail, Star,
  UserCog, Settings, Globe, ChevronLeft, ChevronRight,
  LogOut, Menu, Bell, Search, ExternalLink, BarChart3,
  Tag,
  Info
} from 'lucide-react';

const NAV = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/donations', label: 'Donations', icon: DollarSign },
  { path: '/admin/volunteers', label: 'Volunteers', icon: Users },
  { path: '/admin/partners', label: 'Partners', icon: Handshake },
  { path: '/admin/programs', label: 'Programs', icon: BookOpen },
  { path: '/admin/program-categories', label: 'Programs Category', icon: Tag },
  { path: '/admin/blog', label: 'Blog / News', icon: Newspaper },
  { path: '/admin/gallery', label: 'Gallery', icon: Image },
  { path: '/admin/grants', label: 'Grants', icon: Award },
  { path: '/admin/contacts', label: 'Messages', icon: MessageSquare },
  { path: '/admin/newsletter', label: 'Newsletter', icon: Mail },
  { path: '/admin/testimonials', label: 'Testimonials', icon: Star },
  { path:'/admin/analytics',    label:'Analytics Data', icon:BarChart3 },
  { path:'/admin/about',         label:'About Page',     icon:Info },
  { path: '/admin/users', label: 'Admin Users', icon: UserCog },
  { path: '/admin/settings', label: 'Site Settings', icon: Settings },
];

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path, exact) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const Sidebar = ({ mobile = false }) => (
    <aside className={`${mobile ? 'w-72' : collapsed ? 'w-16' : 'w-64'} bg-royal-950 flex flex-col flex-shrink-0 transition-all duration-300 ${mobile ? '' : 'hidden lg:flex'}`}>
      {/* Brand */}
      <div className={`flex items-center gap-3 p-4 border-b border-white/10 ${collapsed && !mobile ? 'justify-center' : ''}`}>
        <img src="/logo.jpeg" alt="SAAI" className="w-8 h-8 rounded-full object-cover ring-1 ring-gold-400/40 flex-shrink-0" />
        {(!collapsed || mobile) && (
          <div className="min-w-0">
            <div className="text-white font-bold text-sm truncate">SAAI Admin</div>
            <div className="text-gold-400 text-[10px] uppercase tracking-widest">Dashboard</div>
          </div>
        )}
        {!mobile && (
          <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-white/40 hover:text-white p-1 rounded transition-colors flex-shrink-0">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
        {NAV.map(item => {
          const active = isActive(item.path, item.exact);
          return (
            <Link key={item.path} to={item.path}
              onClick={() => mobile && setMobileOpen(false)}
              title={collapsed && !mobile ? item.label : ''}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group ${
                active
                  ? 'bg-gold-500 text-royal-950'
                  : 'text-white/60 hover:text-white hover:bg-white/8'
              } ${collapsed && !mobile ? 'justify-center' : ''}`}>
              <item.icon size={18} className="flex-shrink-0" />
              {(!collapsed || mobile) && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={`p-3 border-t border-white/10 space-y-1 ${collapsed && !mobile ? 'flex flex-col items-center' : ''}`}>
        <a href="/" target="_blank" rel="noreferrer"
          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/8 transition-colors ${collapsed && !mobile ? 'justify-center' : ''}`}
          title={collapsed && !mobile ? 'View Site' : ''}>
          <ExternalLink size={16} />
          {(!collapsed || mobile) && 'View Website'}
        </a>
        <button onClick={handleLogout}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors ${collapsed && !mobile ? 'justify-center' : ''}`}
          title={collapsed && !mobile ? 'Logout' : ''}>
          <LogOut size={16} />
          {(!collapsed || mobile) && 'Logout'}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-gray-50 font-body overflow-hidden">
      <Sidebar />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10 animate-slide-right">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-gray-100 flex items-center gap-4 px-4 lg:px-6 flex-shrink-0">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-600">
            <Menu size={20} />
          </button>
          <div className="flex-1 max-w-sm hidden sm:block">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="w-full pl-9 pr-4 py-1.5 bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:bg-white transition-all" placeholder="Search..." />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-royal-800 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {admin?.name?.[0] || 'A'}
              </div>
              <div className="hidden sm:block">
                <div className="text-sm font-semibold text-gray-800 leading-none">{admin?.name}</div>
                <div className="text-xs text-gray-400 capitalize mt-0.5">{admin?.role}</div>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
